/**
 * Arrivals API — mirror of departuresApi for the ARRIVAL event
 * stream emitted by gen5_edge_processor_v2 to v2_tag_event. Each
 * row is one tag that arrived at a smartbox and was reliably read.
 *
 * Gate semantics:
 *   - NEW_ARRIVAL  — EPC never seen before by this SCU.
 *   - RE_ARRIVAL   — EPC re-arriving after a prior DEPARTURE event.
 *                    On the Departures ledger this also produces a
 *                    CANCEL row when the prior departure was CONFIRMED
 *                    (a "false positive" reversal — see DepartureEpcRow).
 *
 * All roles can read; the server scopes by SCU access. Vendor
 * sessions additionally see only EPCs whose item belongs to their
 * catalog.
 */
import { apiFetch } from "./apiClient";

export type ArrivalGate = "NEW_ARRIVAL" | "RE_ARRIVAL";

/** Pipeline reliability classifier output. HEALTHY / MOSTLY_READ
 *  imply ≥2 confident reads; FLICKERY / UNRELIABLE / UNKNOWN are
 *  weaker — surfaced as the Confidence pill on the page. */
export type ArrivalReliability =
  | "HEALTHY"
  | "MOSTLY_READ"
  | "FLICKERY"
  | "UNRELIABLE"
  | "UNKNOWN";

export interface ArrivalRow {
  tag_event_id: number;
  tag_id: number | null;
  epc: string;
  gate: ArrivalGate;
  event_time: string | null;
  reliability_bucket: ArrivalReliability | string | null;
  tag_quality: string | null;
  /** Set when the arrival correlated to a camera person-episode
   *  (someone was in front of the box at the time). */
  person_episode_id: number | null;
  person_event_time: string | null;
  item_id: number | null;
  sku: string | null;
  item_name: string | null;
  vendor_company_id: number | null;
  vendor_name: string | null;
  scu_name: string;
  smartbox_code: string | null;
  work_center: string | null;
  /** Cancellation flag — only ever true on RE_ARRIVAL rows whose
   *  matching prior CONFIRMED Departure falls in the same week-window
   *  for the owning customer. NEW_ARRIVAL rows are never cancelled. */
  is_cancelled: boolean;
  /** The matching prior Departure event_id when this is a paired
   *  RE_ARRIVAL. NULL for NEW_ARRIVAL or unmatched RE_ARRIVAL. */
  paired_event_id: number | null;
  paired_event_time: string | null;
}

export interface ArrivalsListResponse {
  data: ArrivalRow[];
  pagination: { total: number; limit: number; offset: number };
}

async function req<T>(token: string, path: string): Promise<T> {
  return apiFetch<T>(path, token);
}

export function listArrivals(
  token: string,
  q: {
    scu?: string; since?: string; until?: string; epc?: string;
    limit?: number; offset?: number;
  } = {},
): Promise<ArrivalsListResponse> {
  const params = new URLSearchParams();
  if (q.scu) params.set("scu", q.scu);
  if (q.since) params.set("since", q.since);
  if (q.until) params.set("until", q.until);
  if (q.epc) params.set("epc", q.epc);
  if (q.limit != null) params.set("limit", String(q.limit));
  if (q.offset != null) params.set("offset", String(q.offset));
  const qs = params.toString() ? `?${params.toString()}` : "";
  return req(token, `/api/arrivals${qs}`);
}
